// The sheet both journal modals sit in: bottom sheet on a phone, centred modal from 720px up.
//
// Escape and a click on the backdrop close it, Tab is kept inside it, and focus returns to whatever
// opened it. Behind this sheet is a page of buttons that add, remove and delete; tabbing into them
// while a modal is open is how someone changes something they cannot see.

import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export function JournalSheet({
  title,
  confirmLabel,
  onConfirm,
  onClose,
  footer,
  children,
}: {
  title: ReactNode;
  confirmLabel: string;
  onConfirm(): void;
  onClose(): void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const returnTo = useRef<Element | null>(null);

  useEffect(() => {
    returnTo.current = document.activeElement;
    const field = panel.current?.querySelector<HTMLElement>('input, textarea');
    (field ?? panel.current)?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = panel.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input, textarea, a[href], [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      (returnTo.current as HTMLElement | null)?.focus?.();
    };
  }, [onClose]);

  // Portalled to the top level, like the app's other sheets: the screen's scroller is its own
  // stacking context, and inside it the navigation pill paints over a sheet whatever its z-index
  // says. This is the third time that has bitten this codebase.
  return createPortal(
    <div
      className="csheet"
      role="dialog"
      aria-modal="true"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="csheet-panel" ref={panel} tabIndex={-1}>
        <div className="csheet-grab" />
        <div className="csheet-head">
          <button className="csheet-x" onClick={onClose}>
            Cancel
          </button>
          <span className="csheet-ttl">{title}</span>
          <button className="csheet-do" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
        {children}
        {footer ? <div className="csheet-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
